import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import InfoHelpModal from "./InfoHelpModal";

const CHART_OPTIONS = {
  "cat/cat": [{ id: "grouped_bar", label: "Bar plot" }],
  "num/num": [
    { id: "density", label: "Density plot" },
    { id: "scatter_marginal", label: "Scatterplot w/ marginal points" },
    { id: "density_2d", label: "2D density plot" },
  ],
  "cat/num": [{ id: "density", label: "Density plot" }],
  "num/cat": [{ id: "density", label: "Density plot" }],
};

const OUTCOME_ONLY_OPTIONS = {
  cat: [{ id: "grouped_bar", label: "Bar plot" }],
  num: [
    { id: "density", label: "Density plot" },
    { id: "hist_stacked", label: "Histogram" },
  ],
};

const resolveChartOptions = ({ hasComparison, indepType, depType }) => {
  const normalizedDepType = String(depType || "").toLowerCase();
  if (!normalizedDepType) return [];

  if (!hasComparison) {
    return OUTCOME_ONLY_OPTIONS[normalizedDepType] || [];
  }

  const normalizedIndepType = String(indepType || "").toLowerCase();
  if (!normalizedIndepType) return [];

  const pairKey = `${normalizedIndepType}/${normalizedDepType}`;
  if (pairKey === "cat/cat") {
    return [{ id: "grouped_bar", label: "Bar plot" }];
  }
  if (pairKey === "cat/num" || pairKey === "num/cat") {
    return [{ id: "density", label: "Density plot" }];
  }
  return CHART_OPTIONS[pairKey] || [];
};

const ChartTypeSelector = ({
  indepType,
  depType,
  hasComparison = false,
  chartType,
  setChartType,
}) => {
  const options = useMemo(
    () => resolveChartOptions({ hasComparison, indepType, depType }),
    [hasComparison, indepType, depType]
  );

  useEffect(() => {
    if (!options.length) {
      if (chartType) setChartType("");
      return;
    }
    if (chartType && !options.some((option) => option.id === chartType)) {
      setChartType("");
    }
  }, [options, chartType, setChartType]);

  const handleChartTypeChange = (e) => {
    setChartType(e.target.value);
  };

  if (!depType) {
    return (
      <div className="px-4 py-3 rounded-xl bg-[#f5f0f0]">
        <h3 className="text-lg font-bold mb-1">
          Chart Type <span className="text-red-600">*</span>
        </h3>
        <p className="text-sm text-gray-600">Select an outcome to see available chart types.</p>
      </div>
    );
  }

  if (options.length === 0) {
    return (
      <div className="px-4 py-3 rounded-xl bg-[#f5f0f0]">
        <h3 className="text-lg font-bold mb-1">
          Chart Type <span className="text-red-600">*</span>
        </h3>
        <p className="text-sm text-gray-600">No chart types available for this variable setup.</p>
      </div>
    );
  }

  return (
    <div className="px-4 py-3">
      <h3 className="text-lg font-bold mb-4">
        Chart Type <span className="text-red-600">*</span>
      </h3>
      <div className="flex flex-col gap-3" role="radiogroup" aria-label="Chart Type">
        {options.map((opt) => (
          <div
            key={opt.id}
            className="flex items-center gap-2 border border-[#e7dada] p-4 rounded-xl"
          >
            <label
              className="flex flex-1 cursor-pointer items-center gap-4"
              htmlFor={`chart-type-${opt.id}`}
            >
              <input
                id={`chart-type-${opt.id}`}
                type="radio"
                name="chartType"
                value={opt.id}
                checked={chartType === opt.id}
                onChange={handleChartTypeChange}
                className="h-5 w-5 border-2 border-[#e7dada] checked:border-[#181010]"
              />
              <p className="text-[#181010] text-sm font-medium">{opt.label}</p>
            </label>
            {opt.id === "density" && (
              <InfoHelpModal
                ariaLabel="Show help for density plot"
                title="Density Plot"
                buttonClassName="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#181010]/20"
                iconClassName="h-[15px] w-[15px]"
                modalMaxWidthClassName="max-w-xl"
              >
                <p>
                  A density plot is a smoothed, continuous representation of a numerical variable's
                  distribution.
                </p>
              </InfoHelpModal>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

ChartTypeSelector.propTypes = {
  indepType: PropTypes.string,
  depType: PropTypes.string,
  hasComparison: PropTypes.bool,
  chartType: PropTypes.string.isRequired,
  setChartType: PropTypes.func.isRequired,
};

export default ChartTypeSelector;
